/**
 * 현장 설정 파일 하나를 열어 고치고 배포 브랜치에 PR 로 머지한다.
 *
 * 읽을 때 받은 sha 를 그대로 돌려보내 — 그사이 다른 사람이 같은 파일을 고쳤으면 서버가
 * 409 로 막는다. 그때는 고친 내용은 그대로 두고 [최신 내용 불러오기] 로 다시 시작하게 한다
 * (덮어쓰기 버튼은 두지 않는다 — 남이 고친 현장 설정을 모르고 지우는 게 더 위험하다).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { SiteConfigError, SiteConfigService, type SiteConfigContent } from '../../../services/SiteConfigService';
import type { SiteLiveConfigFile } from '../../../services/SiteHealthService';
import { useDashboardTokens } from '../../../views/SiteDashboardView/tokens';
import LiveConfigPanel from './LiveConfigPanel';

export interface SiteConfigEditorTarget {
  code: string;
  name: string;
  label: string;
  /** 서비스 행에서 열었으면 그 서비스·방재실과 현장 점검이 읽은 실제 파일 — 공통 설정은 없음. */
  app?: string;
  room?: string;
  live?: SiteLiveConfigFile;
}

interface Props {
  projectId: number;
  branch: string;
  target: SiteConfigEditorTarget | null;
  onClose: () => void;
  /** 커밋이 끝났다 — 목록을 다시 읽고 안내를 띄운다. */
  onSaved: (target: SiteConfigEditorTarget) => void;
}

const SiteConfigEditorDialog: React.FC<Props> = ({ projectId, branch, target, onClose, onSaved }) => {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  const { type } = useDashboardTokens();

  const [file, setFile] = useState<SiteConfigContent | null>(null);
  const [text, setText] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  const load = useCallback(async () => {
    if (!target) return;
    setLoading(true);
    setError(null);
    setConflict(false);
    try {
      const f = await SiteConfigService.get(projectId, target.code, target.name);
      setFile(f);
      setText(f.content);
    } catch (e) {
      setError(e instanceof Error ? e.message : '설정 파일을 읽지 못했습니다');
    } finally {
      setLoading(false);
    }
  }, [projectId, target]);

  useEffect(() => {
    setFile(null);
    setText('');
    setMessage('');
    if (target) void load();
  }, [target, load]);

  const dirty = file !== null && text !== file.content;

  const save = async () => {
    if (!target || !file) return;
    setSaving(true);
    setError(null);
    try {
      await SiteConfigService.save(projectId, target.code, target.name, { content: text, sha: file.sha, message });
      onSaved(target);
    } catch (e) {
      setConflict(e instanceof SiteConfigError && e.status === 409);
      setError(e instanceof Error ? e.message : '저장하지 못했습니다');
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    if (dirty && !window.confirm('저장하지 않은 수정이 있습니다. 닫을까요?')) return;
    onClose();
  };

  return (
    <Dialog open={target !== null} onClose={close} fullScreen={narrow} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 0.5 }}>
        {target?.label}
        <Typography component="div" variant="caption" color="text.secondary" sx={{ fontFamily: type.mono.family, wordBreak: 'break-all' }}>
          {file?.path ?? `docker/deploy/sites/${target?.code}/${target?.name}`}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Stack spacing={2}>
            {target?.app && (
              <LiveConfigPanel
                key={`${target.code}-${target.name}-${target.app}`}
                projectId={projectId}
                room={target.room ?? ''}
                app={target.app}
                name={target.name}
                live={target.live}
                repo={file}
                disabled={saving}
                onUse={setText}
              />
            )}
            {file && !file.exists && (
              <Alert severity="info">아직 없는 파일입니다. 저장하면 새로 만듭니다.</Alert>
            )}
            {error && (
              <Alert
                severity="error"
                action={
                  conflict ? (
                    <Button color="inherit" size="small" onClick={() => void load()}>
                      최신 내용 불러오기
                    </Button>
                  ) : undefined
                }
              >
                {error}
                {conflict && ' 불러오면 지금 고친 내용은 사라지니, 필요하면 먼저 복사해 두세요.'}
              </Alert>
            )}
            <TextField
              value={text}
              onChange={(e) => setText(e.target.value)}
              multiline
              minRows={narrow ? 14 : 18}
              maxRows={narrow ? undefined : 30}
              fullWidth
              disabled={saving || !file}
              inputProps={{
                spellCheck: false,
                'aria-label': `${target?.name ?? ''} 내용`,
                'data-testid': 'site-config-editor',
                // 폰에서 입력 시 확대되지 않게 16px.
                style: { fontFamily: type.mono.family, fontSize: 16, lineHeight: 1.5, whiteSpace: 'pre' },
              }}
            />
            <TextField
              label="수정 사유 (커밋 메시지)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="예: 통로등 꺼짐 밝기 0 → 10"
              fullWidth
              disabled={saving || !file}
              inputProps={{ style: { fontSize: 16 }, 'data-testid': 'site-config-message' }}
            />
            <Typography variant="caption" color="text.secondary">
              저장하면 <b>{branch}</b> 에서 작업 브랜치를 새로 만들어 커밋하고, PR 로 <b>{branch}</b> 에 머지합니다. 현장에는 다음 배포 때 반영됩니다.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={close} disabled={saving} sx={{ minHeight: 44 }}>
          닫기
        </Button>
        <Button
          variant="contained"
          onClick={() => void save()}
          disabled={!dirty || saving || loading}
          sx={{ minHeight: 44 }}
          data-testid="site-config-save"
        >
          {saving ? '커밋·머지 중…' : '저장 (PR 머지)'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SiteConfigEditorDialog;
